/**
 * RV Rent Pro SDK loader stub.
 *
 * This tiny bundle is served at the public `rv-rent-sdk[suffix].bundle.min.js` URL (the
 * one partners embed). It does NOT contain the widget. On init() it, in order:
 *   1. paints the skeleton immediately (instant perceived paint, before the core loads),
 *   2. fires the first `essential-data` fetch in parallel with the core download,
 *   3. queues the init config, then injects the heavy core chunk.
 * When the core (`rv-rent-sdk-core[suffix].bundle.min.js`) loads, it reuses the painted
 * mount + the in-flight fetch and drains the queue.
 *
 * Transparent + backward-compatible: partners keep their existing `<script src=...>` +
 * `RVRentSDK.init({...})`. Steps 1-2 are best-effort; any failure is swallowed and the
 * core still loads and self-initializes (degrading to the pre-stub behavior). The core
 * injection is the one must-succeed step.
 *
 * Kept tiny: imports only the no-React `createSdkMount` (skeleton/shadow), `initApi`
 * (fetch wrappers) and the attribution capture. Must NOT import the React app or the
 * Tailwind CSS placeholder.
 *
 * Attribution is captured at script-parse time (see below), so this file is also the
 * script merchants embed site-wide, on landing pages that never call init().
 */

import { captureAttribution } from '~/core/generic/utils/trafficAttribution';

import { initApi } from './api';
import { API_BASE_URL } from './config';
import { createSdkMount, isMountLive, SdkMount } from './mount';

declare const __SDK_CORE_URL__: string;
declare const __SDK_BUILD_ID__: string;

interface LoaderConfig {
  target: string;
  apiKey: string;
  layout?: 'Vertical Layout' | 'Small Card Layout' | 'Big Card Layout';
  [key: string]: unknown;
}

interface SdkStash {
  mounts: Record<string, SdkMount>;
  essential?: { apiKey: string; promise: Promise<unknown> };
}

// Captured at parse time (currentScript is only valid here, not inside init()).
const LOADER_SRC =
  (typeof document !== 'undefined' &&
    (document.currentScript as HTMLScriptElement | null)?.src) ||
  '';

let coreRequested = false;

function getStash(): SdkStash {
  const w = window as unknown as { __rvRentSDK?: SdkStash };
  if (!w.__rvRentSDK) {
    w.__rvRentSDK = { mounts: {} };
  }
  return w.__rvRentSDK;
}

// When the URL carries a date range, the core fetches an availability-filtered
// (dated) essential-data. We don't replicate the core's date->params formatting here,
// so we skip the no-dates prefire in that case to avoid firing a second, mismatched
// request. The parallel-fetch win still applies to the common no-dates first paint.
function urlHasDateRange(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('departure') || params.get('checkOutDate');
    const to = params.get('return') || params.get('checkInDate');
    return !!(from && to);
  } catch {
    return false;
  }
}

function resolveCoreSrc(): string {
  const canonical =
    (typeof __SDK_CORE_URL__ === 'string' && __SDK_CORE_URL__) ||
    `${API_BASE_URL}/sdk/dist/react/rv-rent-sdk-core.bundle.min.js`;

  // Prefer the loader's own path so the -local/-testing variants stay paired.
  // But WordPress optimizers (WP Rocket `data-minify="1"`, Autoptimize, LiteSpeed)
  // re-host a minified copy of the loader on the merchant's own domain under
  // /wp-content/cache/min/; they never mirror the core chunk, so deriving the core
  // URL from that copy 404s. Only trust LOADER_SRC while it's still on our origin.
  // Local builds have no pinned origin to compare against (config.ts), so the
  // loader's own path is the only right answer there.
  if (LOADER_SRC && LOADER_SRC.indexOf('rv-rent-sdk') !== -1) {
    try {
      const loader = new URL(LOADER_SRC);
      if (
        __SDK_SELF_ORIGIN__ ||
        loader.origin === new URL(canonical).origin
      ) {
        // Drop the host page's query (sdk-test `?v=<ts>`, WordPress `?ver=`).
        // The details chunk imports the core as `name?v=<build id>`; any other
        // query makes the browser evaluate the core twice (two Reacts, "S.H is null").
        return (loader.origin + loader.pathname).replace(
          'rv-rent-sdk',
          'rv-rent-sdk-core',
        );
      }
    } catch {
      return canonical;
    }
  }
  return canonical;
}

function loadCore(): void {
  if (coreRequested) {
    return;
  }
  coreRequested = true;
  try {
    const script = document.createElement('script');
    // The core is an ES module (the details page is a second chunk it imports).
    // Its file name never changes; the build id query gives the CDN an
    // immutable cache entry per build (vercel.json).
    script.type = 'module';
    script.src = `${resolveCoreSrc()}?v=${__SDK_BUILD_ID__}`;
    // Opt out of page optimizers (WP Rocket, LiteSpeed, Cloudflare Rocket
    // Loader) that rewrite script tags: a module re-emitted as a classic
    // script throws on its first import.
    script.setAttribute('data-no-minify', '1');
    script.setAttribute('data-no-optimize', '1');
    script.setAttribute('data-cfasync', 'false');
    script.setAttribute('nowprocket', '');
    script.onerror = () =>
      console.error('[RV Rent SDK] Failed to load core bundle:', script.src);
    document.head.appendChild(script);
  } catch (error) {
    console.error('[RV Rent SDK] Failed to inject core bundle:', error);
  }
}

function init(config: LoaderConfig): void {
  const w = window as unknown as { RVRentSDK: { q?: LoaderConfig[] } };

  // Queue the call so the core can replay it once it loads.
  (w.RVRentSDK.q = w.RVRentSDK.q || []).push(config);

  // 1. Instant skeleton (optimization, non-fatal on failure).
  try {
    if (config && config.target) {
      const stash = getStash();
      if (!isMountLive(stash.mounts[config.target], config.target)) {
        // QA-1574: delete BEFORE rebuilding so a createSdkMount throw can't
        // leave the detached mount behind for the core to reuse.
        delete stash.mounts[config.target];
        stash.mounts[config.target] = createSdkMount(config);
      }
    }
  } catch {
    // skeleton is best-effort; the core will create its own mount if needed
  }

  // 2. Prefire the first (no-dates) data fetch, in parallel with the core download.
  //    Skip when the URL has a date range; the core will fetch a dated, availability-
  //    filtered essential-data that this no-dates prefire wouldn't match (would double-fetch).
  try {
    if (config && config.apiKey && !urlHasDateRange()) {
      const stash = getStash();
      if (!stash.essential) {
        const api = initApi();
        stash.essential = {
          apiKey: config.apiKey,
          promise: Promise.resolve(api.fetchEssentialData(config.apiKey)),
        };
      }
    }
  } catch {
    // prefetch is best-effort; the core will fetch normally if this is absent
  }

  // 3. Always load the heavy core (drains the queue, reuses the mount + fetch).
  loadCore();
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

/**
 * Capture attribution on script load, NOT inside init().
 *
 * A paid click id (`?gclid=`) only exists in the URL of the page the visitor
 * actually landed on. Merchants run ads to a homepage or landing page and put
 * the widget somewhere else, so capturing at widget-mount time saw a URL with
 * no click id and an internal referrer, and recorded the visit as `direct`.
 * Embedding this loader site-wide captures the click at its source; init() is
 * what pulls the heavy core, so a bare include stays cheap.
 */
function captureAttributionOnLoad(): void {
  try {
    captureAttribution({
      internalDomains: [hostOf(API_BASE_URL)],
      // Merchant's own domain: their Google Ads / Meta / UET cookies are safe
      // to read, and recover clicks that landed on a page we never ran on.
      recoverClickIdsFromAdCookies: true,
      siteCookie: true,
    });
  } catch {
    // Attribution must never break the merchant's page.
  }
}

// Keep in step with SDK_API_VERSION; the loader and core deploy together.
const RVRentSDK = { VERSION: '1.1.0', q: [] as LoaderConfig[], init };

if (typeof window !== 'undefined') {
  (window as unknown as { RVRentSDK: typeof RVRentSDK }).RVRentSDK = RVRentSDK;
  captureAttributionOnLoad();
}

export default RVRentSDK;
